import { makeStyles } from '@fluentui/react-components';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICalculationOptions, IWhitePaperColumn } from '../../../model';
import { customPalette } from '../../../theme';
import { CalculationOptionsTablistListComponent, ICalculationOptionChange } from './CalculationOptionsTabListComponent';

export interface ICalculationOptionsTabListProps {
    calculationOptions: ICalculationOptions[];
    columns?: IWhitePaperColumn[] | null;
    onCellEdit?: (rowNumber: string | null, columnId: number, newValue: string) => void;
    isEditingEnabled?: boolean;
    onGridReady?: () => void;
    pendingChanges?: Map<string, ICalculationOptionChange>;
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '75vh',
        gap: '12px',
    },
    mainContent: {
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        overflow: 'hidden',
        borderTop: `1px solid ${customPalette.greyD1D3D4}`,
    },
    tabListContainer: {
        minWidth: '200px',
        paddingRight: '16px',
        paddingTop: '12px',
        overflowY: 'auto',
        borderRight: `1px solid ${customPalette.greyD1D3D4}`,
    },
    contentArea: {
        flex: 1,
        overflow: 'auto',
        paddingTop: '12px',
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingBottom: '16px',
    },
    optionRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 100px',
        alignItems: 'center',
        minHeight: '32px',
        padding: '8px',
        borderBottom: `1px solid ${customPalette.greyD1D3D4}`,
        ':hover': {
            backgroundColor: customPalette.statusCoolMidGrey,
        },
    },
    optionName: {
        lineHeight: '32px',
    },
    optionValue: {
        textAlign: 'center',
        minHeight: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    contentTitle: {
        fontSize: '15px',
        fontWeight: 500,
        marginBottom: '12px',
        paddingBottom: '8px',
        borderBottom: `1px solid ${customPalette.greyD1D3D4}`,
    },
});

const CalculationOptionsTabList: FC<ICalculationOptionsTabListProps> = (props) => {
    const { calculationOptions, columns, onCellEdit, isEditingEnabled, onGridReady, pendingChanges } = props;
    const { t } = useTranslation();
    const styles = useStyles();

    const hasCalculationOptions = calculationOptions?.length;
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTab, setSelectedTab] = useState<string>(columns?.[0]?.fieldName || '');
    const contentAreaRef = useRef<HTMLDivElement>(null);

    const getTabStyle = (isSelected: boolean): React.CSSProperties => ({
        borderRadius: '4px',
        backgroundColor: isSelected ? customPalette.blueGreyNeutralTint2 : 'transparent',
        color: isSelected ? customPalette.blue075A92 : 'inherit',
    });

    const filteredOptions = useMemo(() => {
        if (!searchQuery.trim()) {
            return calculationOptions;
        }

        const lowerQuery = searchQuery.toLowerCase();
        const filtered = calculationOptions.filter((option) => {
            if (option.name?.toLowerCase().includes(lowerQuery)) {
                return true;
            }
            if (option.value && columns) {
                return columns.some((column) => {
                    if (column.fieldName) {
                        const fieldValue = option.value?.[column.fieldName]?.current?.toString() || '';
                        return fieldValue.toLowerCase().includes(lowerQuery);
                    }
                    return false;
                });
            }
            return false;
        });

        return filtered.length > 0 ? filtered : calculationOptions;
    }, [calculationOptions, searchQuery, columns]);

    const filteredColumns = useMemo(() => {
        if (!searchQuery.trim() || !columns) {
            return columns || [];
        }

        const lowerQuery = searchQuery.toLowerCase();
        const filtered = columns.filter((column) => {
            if (!column.fieldName) return false;
            const tabText = column.entityName || column.headingFirstLine || column.fieldName;
            return tabText.toLowerCase().includes(lowerQuery);
        });

        return filtered.length > 0 ? filtered : columns || [];
    }, [columns, searchQuery]);

    const selectedColumn = columns?.find((col) => col.fieldName === selectedTab);

    useEffect(() => {
        if (onGridReady) {
            onGridReady();
        }
    }, [onGridReady]);

    useEffect(() => {
        if (columns && columns.length > 0 && !selectedTab) {
            setSelectedTab(columns[0].fieldName || '');
        }
    }, [columns, selectedTab]);

    useEffect(() => {
        if (contentAreaRef.current) {
            contentAreaRef.current.scrollTop = 0;
        }
    }, [selectedTab, searchQuery]);

    if (!hasCalculationOptions) {
        return <div>{t('noOptionsCanBeDisplayed')}</div>;
    }

    if (!columns || columns.length === 0) {
        return <div>{t('noColumnsAvailable')}</div>;
    }

    return (
        <CalculationOptionsTablistListComponent
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            filteredColumns={filteredColumns}
            filteredOptions={filteredOptions}
            selectedColumn={selectedColumn}
            pendingChanges={pendingChanges}
            onCellEdit={onCellEdit}
            isEditingEnabled={isEditingEnabled}
            styles={styles}
            getTabStyle={getTabStyle}
            customPalette={customPalette}
            t={t}
            contentAreaRef={contentAreaRef}
        />
    );
};

export default CalculationOptionsTabList;
